import { useState } from "react";
import {
  useListMemberCharges, getListMemberChargesQueryKey,
  useUpdateMemberCharge,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { MoreHorizontal, CheckCircle, XCircle, UserX, CalendarClock, Loader2 } from "lucide-react";
import { CancelBookingDialog } from "./cancel-booking-dialog";
import { useAuth } from "@/context/auth-context";
import { toast } from "sonner";
import type { MemberCharge } from "@workspace/api-client-react";

interface Props { memberId: number; }

type FilterChip = "all" | "pending" | "paid" | "cancelled" | "no_show" | "rescheduled";

function BookingStatusBadge({ charge }: { charge: MemberCharge }) {
  const status = charge.status;
  const desc = charge.description ?? "";
  if (status === "Waived" && desc.startsWith("[Cancelled]")) {
    return <Badge className="bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-100">Cancelled</Badge>;
  }
  if (desc.startsWith("[No-Show]")) {
    return <Badge className="bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-100">No-Show</Badge>;
  }
  if (desc.startsWith("[Rescheduled")) {
    return <Badge className="bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-50">Rescheduled</Badge>;
  }
  if (status === "Paid") {
    return <Badge className="bg-green-100 text-green-700 border border-green-200 hover:bg-green-100">Paid</Badge>;
  }
  if (status === "Pending") {
    return <Badge variant="outline">Pending</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
}

function filterCharge(charge: MemberCharge, filter: FilterChip): boolean {
  const desc = charge.description ?? "";
  if (filter === "all") return true;
  if (filter === "pending") return charge.status === "Pending";
  if (filter === "paid") return charge.status === "Paid";
  if (filter === "cancelled") return charge.status === "Waived" && desc.startsWith("[Cancelled]");
  if (filter === "no_show") return desc.startsWith("[No-Show]");
  if (filter === "rescheduled") return desc.startsWith("[Rescheduled");
  return true;
}

export function BookingsTab({ memberId }: Props) {
  const [filter, setFilter] = useState<FilterChip>("all");
  const [actionDialog, setActionDialog] = useState<null | { id: number; action: "cancel" | "no_show" | "reschedule"; charge: MemberCharge }>(null);
  const [markPaidId, setMarkPaidId] = useState<number | null>(null);
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // A member's booking history is shown in full — fetch all (high pageSize), not one page.
  const chargesParams = { memberId, pageSize: 200 };
  const { data: chargePage } = useListMemberCharges(chargesParams, {
    query: { queryKey: getListMemberChargesQueryKey(chargesParams) },
  });
  const charges = chargePage?.items ?? [];

  const updateCharge = useUpdateMemberCharge({
    mutation: {
      onSuccess: () => {
        toast.success("Charge marked as paid.");
        qc.invalidateQueries({ queryKey: getListMemberChargesQueryKey({ memberId }) });
        setMarkPaidId(null);
      },
      onError: (err) => toast.error(`Mark Paid failed: ${(err as Error).message}`),
    },
  });

  const bookings = (charges ?? []).filter(
    (c) => c.sourceType === "Hourly_Booking" || c.sourceType === "Daily_Booking"
  );
  const filtered = bookings.filter((c) => filterCharge(c, filter));

  const filterLabels: Record<FilterChip, string> = {
    all: "All", pending: "Pending", paid: "Paid",
    cancelled: "Cancelled", no_show: "No-Show", rescheduled: "Rescheduled",
  };

  const markPaidCharge = markPaidId != null ? bookings.find((c) => c.id === markPaidId) : null;

  return (
    <div className="space-y-4">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(filterLabels) as FilterChip[]).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "secondary" : "outline"}
            onClick={() => setFilter(f)}
          >
            {filterLabels[f]}
          </Button>
        ))}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Receipt #</TableHead>
            <TableHead>Resource</TableHead>
            <TableHead className="w-32">Booked For</TableHead>
            <TableHead className="w-20">Duration</TableHead>
            <TableHead className="w-24 text-right">Amount</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                {bookings.length === 0 ? "No bookings yet" : "No bookings match this filter"}
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs">{c.invoiceReference ?? "—"}</TableCell>
                <TableCell className="text-sm">{c.description}</TableCell>
                <TableCell className="text-xs">
                  {new Date(c.transactionDate).toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                </TableCell>
                <TableCell className="text-sm">
                  {c.quantity}{" "}
                  {c.sourceType === "Hourly_Booking" ? "hrs" : "day"}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  ₹{Number(c.total).toFixed(2)}
                </TableCell>
                <TableCell><BookingStatusBadge charge={c} /></TableCell>
                <TableCell>
                  {c.status === "Pending" && (
                    <div className="flex gap-1 items-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setMarkPaidId(c.id)}
                        aria-label="Mark Paid"
                        title="Mark Paid"
                      >
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label="Booking actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem
                            disabled={!isAdmin}
                            title={!isAdmin ? "Admin only" : undefined}
                            onClick={() => isAdmin && setActionDialog({ id: c.id, action: "cancel", charge: c })}
                            className={!isAdmin ? "opacity-50 cursor-not-allowed" : ""}
                          >
                            <XCircle className="mr-2 h-4 w-4 text-destructive" />
                            Cancel Booking
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!isAdmin}
                            title={!isAdmin ? "Admin only" : undefined}
                            onClick={() => isAdmin && setActionDialog({ id: c.id, action: "no_show", charge: c })}
                            className={!isAdmin ? "opacity-50 cursor-not-allowed" : ""}
                          >
                            <UserX className="mr-2 h-4 w-4 text-orange-600" />
                            Record No-Show
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!isAdmin}
                            title={!isAdmin ? "Admin only" : undefined}
                            onClick={() => isAdmin && setActionDialog({ id: c.id, action: "reschedule", charge: c })}
                            className={!isAdmin ? "opacity-50 cursor-not-allowed" : ""}
                          >
                            <CalendarClock className="mr-2 h-4 w-4 text-blue-600" />
                            Reschedule
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Cancellation / No-Show / Reschedule dialogs */}
      {actionDialog && (
        <CancelBookingDialog
          chargeId={actionDialog.id}
          action={actionDialog.action}
          open
          onClose={() => setActionDialog(null)}
          chargeDescription={actionDialog.charge.description ?? undefined}
          chargeDate={new Date(actionDialog.charge.transactionDate).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
          })}
          chargeAmount={Number(actionDialog.charge.total)}
          memberId={memberId}
        />
      )}

      {/* Mark Paid confirmation dialog */}
      <Dialog open={markPaidId != null} onOpenChange={(o) => { if (!o) setMarkPaidId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark Booking Paid</DialogTitle>
          </DialogHeader>
          {markPaidCharge && (
            <div className="space-y-2">
              <div className="rounded-md bg-muted p-3 text-sm">
                <div>{markPaidCharge.description}</div>
                <div className="font-mono mt-1">₹{Number(markPaidCharge.total).toFixed(2)}</div>
              </div>
              <p className="text-sm text-muted-foreground">
                This will mark the booking charge as Paid.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidId(null)}>Cancel</Button>
            <Button
              disabled={updateCharge.isPending}
              onClick={() => {
                if (markPaidId != null) {
                  updateCharge.mutate({ id: markPaidId, data: { status: "Paid" } });
                }
              }}
            >
              {updateCharge.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
